import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import Spinner from 'react-spinkit';
import { RootState } from '../../ApplicationState';
import { selectClassroom } from '../../Classrooms';
import { getGuidedReadingLevelRange, isUserHasKey } from '../../GlobalFunctions';
import { checkEmailExists } from '../../BackendInterface';
import FormikControl from '../../components/FormikContainer/FormikControl';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import { StudentProxyType } from '../../../peekapak-types/DataProtocolTypes';

interface Props {
  student: StudentProxyType;
  readingLevels: string[];
  onSubmit: (value: FormValues) => void | Promise<void>;
}

interface FormValues {
  name: string;
  readingLevel: string;
  email?: string;
}

const validationSchema = Yup.object({
  name: Yup.string().min(4, 'Student name must be at least 4 characters').required('Required'),
  readingLevel: Yup.string().required('Required'),
  email: Yup.string().email('Please enter a valid email address'),
});

const EMAIL_ALREADY_EXISTS_ERROR = 'This email is already associated with an existing login.';
const EMAIL_CHECK_FAILED_ERROR = "We couldn't verify this email right now. Please try again.";

const EditStudentEditControl = ({ student, readingLevels, onSubmit }: Props): JSX.Element => {
  const [isPending, setIsPending] = useState(false);

  let fullName = student.firstName;
  if (student.middleName) fullName += ' ' + student.middleName;
  if (student.lastName) fullName += ' ' + student.lastName;

  const initialValues: FormValues = {
    name: '' || fullName,
    readingLevel: '' || student.readingLevel,
    email: student.email || '',
  };

  const normalizeEmail = (emailInput?: string) => (emailInput || '').trim().toLowerCase();
  const initialEmail = normalizeEmail(student.email);

  const classroom = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom;
  });

  const currentReadAloudLevelScheme = classroom.readAloudLevelScheme || 'fountasAndPinnell';

  const getMappedReadingLevels = () => {
    return readingLevels.map((level) => {
      const mappedLevel = getGuidedReadingLevelRange(parseInt(level), currentReadAloudLevelScheme);
      return { key: mappedLevel, value: mappedLevel };
    });
  };

  const isMiddleSchool = () => {
    if (classroom.grade && parseInt(classroom.grade) > 5) return true;
    return false;
  };

  return isUserHasKey('KEY_MYPEEKAVILLE_LESSONS') ? (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setFieldError, setSubmitting }) => {
        setIsPending(true);
        const submittedEmail = normalizeEmail(values.email);
        if (submittedEmail && submittedEmail !== initialEmail) {
          try {
            const exists = await checkEmailExists(submittedEmail);

            if (exists) {
              setFieldError('email', EMAIL_ALREADY_EXISTS_ERROR);
              setSubmitting(false);
              setIsPending(false);
              return;
            }
          } catch {
            setFieldError('email', EMAIL_CHECK_FAILED_ERROR);
            setSubmitting(false);
            setIsPending(false);
            return;
          }
        }

        await Promise.resolve(onSubmit(values));
        // note: setIsPending(false) is intentionally omitted here — the modal
        // closes on success so the component unmounts; setting state on an
        // unmounted component is a no-op but suppresses React warnings.
      }}
    >
      {() => (
        <Form>
          <FormikControl
            control='input'
            type='text'
            name='name'
            label='Name'
            onBlur={(event: React.ChangeEvent<HTMLInputElement>) => event.preventDefault()}
          />
          {!isMiddleSchool() && (
            <FormikControl
              control='select'
              label='Reading Level'
              name='readingLevel'
              options={getMappedReadingLevels()}
              onBlur={(event: React.ChangeEvent<HTMLSelectElement>) => event.preventDefault()}
            />
          )}
          <FormikControl
            control='input'
            type='email'
            name='email'
            label='School Email Address'
            onBlur={(event: React.ChangeEvent<HTMLInputElement>) => event.preventDefault()}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
            <PrimaryButton type='submit' disabled={isPending}>
              {isPending ? 'Updating student...' : '+ Update Student'}
            </PrimaryButton>
            {isPending && (
              <div style={{ display: 'flex', alignItems: 'center', height: '1.6rem', overflow: 'hidden' }}>
                <Spinner
                  name='three-bounce'
                  style={{ transform: 'scale(0.4)', transformOrigin: 'left center', width: '4rem' }}
                />
              </div>
            )}
          </div>
        </Form>
      )}
    </Formik>
  ) : (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {() => (
        <Form>
          <FormikControl disabled control='input' type='text' name='name' label='Name' />
          <FormikControl disabled control='input' type='email' name='email' label='School Email Address' />
        </Form>
      )}
    </Formik>
  );
};

export default EditStudentEditControl;
